import { z } from 'zod';

export const AGENTIC_BUSINESS_ROLES = [
  'analyst',
  'manager',
  'director',
  'vp',
  'executive',
] as const;

/** Single source for API + UI; use `z.infer` at call sites that need the union. */
export const agenticBusinessRoleSchema = z.enum(AGENTIC_BUSINESS_ROLES);
export type AgenticBusinessRole = z.infer<typeof agenticBusinessRoleSchema>;

/** Member/org APIs and UI: explicit `null` = no agentic business profile assigned. */
export type AgenticBusinessRoleNullable = AgenticBusinessRole | null;

export const AGENTIC_BUSINESS_ROLE_LABELS = {
  analyst: 'Analyst / Individual Contributor (Level 1)',
  manager: 'Manager / Team Lead (Level 2)',
  director: 'Director / Department Head (Level 3)',
  vp: 'VP / Functional Leader (Level 4)',
  executive: 'Executive (CXO) (Level 5)',
} as const satisfies Record<(typeof AGENTIC_BUSINESS_ROLES)[number], string>;

/** Canonical `{ value, label }` rows for business-role selects (org Members matrix, Agentic AI config, etc.). */
export const AGENTIC_BUSINESS_ROLE_OPTIONS: { value: AgenticBusinessRole; label: string }[] =
  AGENTIC_BUSINESS_ROLES.map((role) => ({
    value: role,
    label: AGENTIC_BUSINESS_ROLE_LABELS[role],
  }));

export const AGENTIC_BEHAVIOR_MODES = [
  'precision',
  'compliance',
  'balanced',
  'optimization',
] as const;

const behaviorModesSchema = z
  .array(z.enum(AGENTIC_BEHAVIOR_MODES))
  .min(1)
  .superRefine((modes, ctx) => {
    if (modes.includes('balanced') && modes.length > 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Balanced cannot be combined with other modes',
      });
    }
    if (modes.includes('optimization') && modes.length > 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Optimization cannot be combined with other modes',
      });
    }
  });

export const agenticRoleConfigWriteSchema = z.object({
  behavior_modes: behaviorModesSchema,
  sensitivity_level: z.number().min(0).max(100),
  markers: z.array(z.string()),
});

export const agenticRoleConfigReadSchema = z.object({
  id: z.string(),
  organization_id: z.string(),
  business_role: agenticBusinessRoleSchema,
  behavior_modes: behaviorModesSchema,
  sensitivity_level: z.number(),
  markers: z.array(z.string()),
});

export const agenticConfigurationResponseSchema = z.object({
  roles: z.record(z.string(), agenticRoleConfigReadSchema),
});

export const agenticConfigurationPutRequestSchema = z.object({
  roles: z.record(z.string(), agenticRoleConfigWriteSchema),
});

export const agenticRuntimeConfigResponseSchema = z.object({
  optimization: z.boolean(),
  mode_name: z.string(),
  locked_model: z.enum(['GPT', 'Gemini', 'Claude', 'Grok', 'Cohere']).nullable(),
});

export type AgenticRoleConfigWrite = z.infer<typeof agenticRoleConfigWriteSchema>;
export type AgenticRoleConfigRead = z.infer<typeof agenticRoleConfigReadSchema>;
export type AgenticConfigurationResponse = z.infer<typeof agenticConfigurationResponseSchema>;
export type AgenticConfigurationPutRequest = z.infer<typeof agenticConfigurationPutRequestSchema>;
export type AgenticRuntimeConfigResponse = z.infer<typeof agenticRuntimeConfigResponseSchema>;
